import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import AccordionComponent from "../../../../common/Accordion";
import ChargesEditTableData from "./ChargesEditTableData";
import {
  INVOICE_LINE_ITEMS,
  STANDARD_CHARGES_INVOICE,
} from "../actions/lineItemsConstant";
import InvoiceLineItemEditTableData from "./InvoiceLineItemEditTableData";
import {
  getInvoiceChangeLog,
  getInvoiceSummaryByCase,
} from "../../../../services/ManageInvoiceAPI";
import { formattedDate } from "../../../../utils/dateUtils";
import ChangeLogDrawer from "../../../Master/Billing/components/Configuration/ChangeLogDrawer";
import BillingDataTable from "../../../Master/Billing/components/BillingDataTable";
import { INVOICE_CHANGE_LOG } from "../actions/constants";

const ViewInvoice = ({ selectedRow, onClose, isCase = false }: any) => {
  const [summaryData, setSummaryData] = useState<any>("");

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showChangeDrawer, setShowChangeDrawer] = useState(false);
  const [changeLogData, setChangeLogData] = useState<any[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const caseInfoId = selectedRow?.caseInfoId;
  const invoiceStatus = selectedRow?.invoiceStatus;

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleInputChange = debounce((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setSearchQuery(value);
    }
  }, 300);

  const handleResetFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setSearchQuery("");
  };

  const getSummaryDetails = async () => {
    try {
      const summary = await getInvoiceSummaryByCase(caseInfoId);
      const changeLog = await getInvoiceChangeLog(caseInfoId);

      if (summary) {
        setSummaryData(summary);
      }
      if (changeLog) {
        setChangeLogData(changeLog || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (caseInfoId) {
      getSummaryDetails();
    }
  }, [caseInfoId]);

  const [viewInvoiceKey, setViewInvoiceKey] = useState<string | null>("0");

  const handleAccordionSelect = (key: string | null) => {
    if (viewInvoiceKey === key) {
      return; // do nothing
    }
    setViewInvoiceKey(key || "0");
  };

  const invoiceAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item) => ({ ...item }))}
      activeKey={viewInvoiceKey}
      onSelect={handleAccordionSelect}
    />
  );

  const handleOpenChangeDrawer = () => {
    setShowTooltip(false);
    setShowChangeDrawer(true);
  };

  const handleCloseChangeDrawer = () => {
    setShowChangeDrawer(false);
  };

  return (
    <>
      <div className="main_wrapper_section appoinment_wrapper mt-3">
        <Container fluid className={!isCase ? "px-5" : ""}>
          <div className="container-fluid">
            <div className="mt-3 mb-3">
              <Row className="align-items-center justify-content-between mb-3">
                <Col xs="auto" className="sectionTitle noborder pe-3 px-0">
                  {!isCase ? "Overview" : ""}
                </Col>

                {/* Right side: Controls */}
                <Col xs="auto" className="px-0">
                  <div className="d-flex align-items-center gap-2">
                    <InputGroup className="w-auto">
                      <InputGroup.Text className="px-2">
                        <span className="material-symbols-outlined text-black-50">
                          search
                        </span>
                      </InputGroup.Text>
                      <Form.Control
                        ref={inputRef}
                        aria-label="Search"
                        onChange={(e) =>
                          handleInputChange(e.target.value.trim())
                        }
                        onPaste={(e) => {
                          e.preventDefault();
                          const text = e.clipboardData.getData("text").trim();
                          handleInputChange(text);
                          if (inputRef.current) inputRef.current.value = text;
                        }}
                      />
                    </InputGroup>

                    {/* Reset Button */}
                    <Button
                      className="d-flex align-items-center"
                      style={{
                        background: "none",
                        border: "none",

                        padding: "10px",
                        fontSize: "22px",
                      }}
                      onClick={handleResetFilters}
                    >
                      <span className="material-icons reset_ic0">
                        restart_alt
                      </span>
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {invoiceAccordion([
            {
              title: "Invoice Details",
              content: (
                <>
                  <Row className="mb-3">
                    <Col sm={12}>
                      <Card>
                        <Card.Body style={{ backgroundColor: "#fffef1" }}>
                          <Row>
                            {/* Section 1 */}
                            <Col md={4} className="pe-3">
                              <div className="d-flex flex-column gap-2">
                                <div className="d-flex">
                                  <strong
                                    className="me-2"
                                    style={{ minWidth: "140px" }}
                                  >
                                    Case ID
                                  </strong>
                                  <span>{summaryData?.caseId}</span>
                                </div>

                                <div className="d-flex">
                                  <strong
                                    className="me-2"
                                    style={{ minWidth: "140px" }}
                                  >
                                    Claim Number
                                  </strong>
                                  <span>{summaryData?.claimNumber}</span>
                                </div>
                              </div>
                            </Col>

                            {/* Section 2 */}
                            <Col md={4} className="px-3 border-start">
                              <div className="d-flex flex-column gap-2">
                                <div className="d-flex">
                                  <strong
                                    className="me-2"
                                    style={{ minWidth: "140px" }}
                                  >
                                    Doctor
                                  </strong>
                                  <span>{summaryData?.qmeDoctorFullName}</span>
                                </div>

                                <div className="d-flex">
                                  <strong
                                    className="me-2"
                                    style={{ minWidth: "140px" }}
                                  >
                                    Case Type
                                  </strong>
                                  <span>{summaryData?.caseReviewTypeName}</span>
                                </div>
                              </div>
                            </Col>

                            {/* Section 3 */}
                            <Col md={4} className="ps-3 border-start">
                              <div className="d-flex flex-column gap-2">
                                <div className="d-flex">
                                  <strong
                                    className="me-2"
                                    style={{ minWidth: "140px" }}
                                  >
                                    Date Of Registration
                                  </strong>
                                  <span>
                                    {formattedDate(
                                      summaryData?.dateOfRegistration,
                                    )}
                                  </span>
                                </div>

                                <div className="d-flex">
                                  <strong
                                    className="me-2"
                                    style={{ minWidth: "140px" }}
                                  >
                                    Patient Name
                                  </strong>
                                  <span>{summaryData?.subjectFullName}</span>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col sm={12}>
                      <Card>
                        <Card.Body style={{ border: "none" }}>
                          <Row>
                            <Col
                              xs="auto"
                              className="fw-semibold form-label mb-2"
                              style={{ color: "#0092d5", fontSize: "13px" }}
                            >
                              Invoice Line Items
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <InvoiceLineItemEditTableData
                                headers={INVOICE_LINE_ITEMS}
                                caseInfoID={caseInfoId}
                                searchQuery={searchQuery}
                                onAddSuccess={getSummaryDetails}
                                invoiceStatus={invoiceStatus}
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col sm={12}>
                      <Card>
                        <Card.Body style={{ border: "none" }}>
                          <Row>
                            <Col
                              className="fw-semibold form-label mb-2"
                              style={{ color: "#0092d5", fontSize: "13px" }}
                            >
                              Standard Charges
                            </Col>
                          </Row>

                          <Row>
                            <Col>
                              <ChargesEditTableData
                                headers={STANDARD_CHARGES_INVOICE}
                                caseInfoID={caseInfoId}
                                searchQuery={searchQuery}
                                onAddSuccess={getSummaryDetails}
                                invoiceStatus={invoiceStatus}
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col sm={6}>
                      <Card>
                        <Card.Body>
                          <div className="table-responsive border-0 mb-2">
                            <Table style={{ width: "100%", marginBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      color: "#0092d5",
                                      fontSize: "13px",
                                      textAlign: "left",
                                      padding: "8px 12px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Total Invoice & Summary
                                  </th>

                                  <th
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                      textAlign: "right",
                                    }}
                                  >
                                    INR
                                  </th>

                                  <th
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                      textAlign: "right",
                                    }}
                                  >
                                    USD
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                <tr style={{ backgroundColor: "#f5f5f5" }}>
                                  <td
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Total Line Items
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    ₹{" "}
                                    {Number(
                                      summaryData.totalAct_InvoicedAmount_INR,
                                    ).toFixed(2)}
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    ${" "}
                                    {Number(
                                      summaryData.totalAct_InvoicedAmount,
                                    ).toFixed(2)}
                                  </td>
                                </tr>

                                <tr>
                                  <td
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Total Custom Line Items
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    ₹{" "}
                                    {Number(
                                      summaryData.totalCus_InvoicedAmount_INR,
                                    ).toFixed(2)}
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    ${" "}
                                    {Number(
                                      summaryData.totalCus_InvoicedAmount,
                                    ).toFixed(2)}
                                  </td>
                                </tr>

                                <tr style={{ backgroundColor: "#f5f5f5" }}>
                                  <td
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Total Additional Charges
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    ₹{" "}
                                    {Number(
                                      summaryData.totalStd_InvoicedAmount_INR,
                                    ).toFixed(2)}
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    ${" "}
                                    {Number(
                                      summaryData.totalStd_InvoicedAmount,
                                    ).toFixed(2)}
                                  </td>
                                </tr>

                                <tr>
                                  <td
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Total Invoice Amount
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#0092d5",
                                    }}
                                  >
                                    ₹{" "}
                                    {Number(
                                      summaryData.total_InvoicedAmount_INR,
                                    ).toFixed(2)}
                                  </td>

                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#0092d5",
                                    }}
                                  >
                                    ${" "}
                                    {Number(
                                      summaryData.total_InvoicedAmount,
                                    ).toFixed(2)}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col sm={6}>
                      <Card>
                        <Card.Body>
                          <Row>
                            <div className="table-responsive border-0 mb-2">
                              <Table style={{ width: "100%", marginBottom: 0 }}>
                                <thead>
                                  <tr>
                                    <th
                                      style={{
                                        color: "#0092d5",
                                        fontSize: "13px",
                                        textAlign: "left",
                                        padding: "8px 12px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Total Pending Invoice & Summary
                                    </th>

                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                        textAlign: "right",
                                      }}
                                    >
                                      INR
                                    </th>

                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                        textAlign: "right",
                                      }}
                                    >
                                      USD
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Total Line Items
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      ₹{" "}
                                      {Number(
                                        summaryData.totalAct_PendingInvoiceAmount_INR,
                                      ).toFixed(2)}
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      ${" "}
                                      {Number(
                                        summaryData.totalAct_PendingInvoiceAmount,
                                      ).toFixed(2)}
                                    </td>
                                  </tr>

                                  <tr>
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Total Custom Line Items
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      ₹{" "}
                                      {Number(
                                        summaryData.totalCus_PendingInvoiceAmount_INR,
                                      ).toFixed(2)}
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      ${" "}
                                      {Number(
                                        summaryData.totalCus_PendingInvoiceAmount,
                                      ).toFixed(2)}
                                    </td>
                                  </tr>

                                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Total Additional Charges
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      ₹{" "}
                                      {Number(
                                        summaryData.totalStd_PendingInvoiceAmount_INR,
                                      ).toFixed(2)}
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      ${" "}
                                      {Number(
                                        summaryData.totalStd_PendingInvoiceAmount,
                                      ).toFixed(2)}
                                    </td>
                                  </tr>

                                  <tr>
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Total Pending Invoice Amount
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#d9534f",
                                      }}
                                    >
                                      ₹{" "}
                                      {Number(
                                        summaryData.total_PendingInvoicedAmount_INR,
                                      ).toFixed(2)}
                                    </td>

                                    <td
                                      style={{
                                        textAlign: "right",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#d9534f",
                                      }}
                                    >
                                      ${" "}
                                      {Number(
                                        summaryData.total_PendingInvoicedAmount,
                                      ).toFixed(2)}
                                    </td>
                                  </tr>
                                </tbody>
                              </Table>
                            </div>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
              ),
            },
          ])}
          <Row className=" mb-3">
            <Col sm={12} className="d-flex align-items-center">
              {
                <OverlayTrigger
                  show={showTooltip}
                  placement="auto"
                  trigger={["hover", "focus"]}
                  delay={{ show: 200, hide: 100 }}
                  overlay={
                    <Tooltip style={{ backgroundColor: "#b3e0ff" }}>
                      Logs
                    </Tooltip>
                  }
                >
                  <div className="d-flex">
                    <Button
                      type="button"
                      className="btn btn-primary d-flex align-items-center mx-2"
                      onClick={handleOpenChangeDrawer}
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                    >
                      <span className="material-icons">description</span>
                    </Button>
                  </div>
                </OverlayTrigger>
              }
              <Button
                variant="outline-info"
                className="d-flex align-items-center me-auto btn-text-border"
                onClick={onClose}
              >
                <span className="material-icons me-1">arrow_back</span>
                Back
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
      <ChangeLogDrawer
        title={"Audit Log"}
        open={showChangeDrawer}
        onClose={handleCloseChangeDrawer}
        placement="bottom"
      >
        <BillingDataTable
          headers={INVOICE_CHANGE_LOG}
          rows={changeLogData}
          isLogTable={true}
        />
      </ChangeLogDrawer>
    </>
  );
};

export default ViewInvoice;
